export interface TimelineNode {
  time: string;
  title: string;
  desc: string;
  tone: 'key' | 'normal';
}

export interface DossierSection {
  heading: string;
  paragraphs: string[];
  quote?: string;
}

export interface Foreshadow {
  title: string;
  desc: string;
}

export interface Connection {
  title: string;
  a: string;
  b: string;
  desc: string;
}

export interface Dossier {
  id: string;
  title: string;
  dimension: string;
  summary: string;
  cover: string;
  author: string;
  date: string;
  readTime: string;
  heat: number;
  tags: string[];
  question: string;
  sections: DossierSection[];
  timeline: TimelineNode[];
  foreshadows: Foreshadow[];
  connections: Connection[];
  takeaways: string[];
}

export const dossiers: Dossier[] = [
  {
    id: 'conference-silence',
    title: '发布会现场的 3 秒停顿：一句没说完的话，如何改写整条叙事',
    dimension: 'details',
    summary:
      '所有人都在讨论被念出来的那半句。真正值得看的，是被咽回去的另外半句，以及停顿发生时台下三个人的反应。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20investigation%20dossier%20aesthetic%2C%20abstract%20folded%20paper%20documents%20and%20red%20string%20connecting%20clippings%20on%20a%20corkboard%2C%20warm%20amber%20lighting%2C%20muted%20cream%20background%2C%20cinematic%20soft%20shadows%2C%20minimalist%20composition%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-01&orientation=landscape',
    author: '拆解组 · 林既',
    date: '2026-08-28',
    readTime: '9 分钟',
    heat: 9820,
    tags: ['现场记录', '措辞分析', '停顿'],
    question: '为什么一句被主动咽回去的话，比说出口的十句更能解释后来的走向？',
    sections: [
      {
        heading: '先看原始记录，而不是转述版本',
        paragraphs: [
          '当晚流出的视频有三个版本，时长不同、剪辑点不同。我们把三个版本逐帧对齐后发现：被广泛传播的那一版，恰好切掉了停顿前 0.8 秒的抬头动作。这个动作说明说话人是在确认台下某个方向的反应，而不是在组织语言。',
          '原始记录显示，那半句话里出现了两个此前从未在该场合使用的限定词。限定词本身不敏感，但它改变了后面所有内容的适用范围——这是最容易被忽略的地方。',
        ],
        quote: '真正改写叙事的，往往不是一个新信息，而是一个把旧信息重新划界的限定词。',
      },
      {
        heading: '停顿发生时，台下有三个反应',
        paragraphs: [
          '第一个反应来自右侧第二排：有人立刻低头看手机，动作幅度很小，但和前二十分钟的坐姿明显不同。第二个反应来自媒体区，两台机器同时调整了机位。第三个反应最隐蔽——工作人员的手从提示板上放下了。',
          '这三个动作指向同一个结论：在场的人对这句话的走向，事前并没有完全达成一致。所谓「临时改口」，更像是现场才完成的最后一次对表。',
        ],
      },
      {
        heading: '它解释了什么',
        paragraphs: [
          '把这段停顿放回整条时间线，后面 72 小时里所有看似矛盾的表态都能对上：一方强调流程，一方强调个人，其实是同一句没说完的话被拆成了两半。',
          '这也是「爆料看点」一直坚持的方法——先确认原始动作，再谈动机。动机可以被包装，动作很难。',
        ],
      },
    ],
    timeline: [
      { time: 'T-00:12', title: '提示板最后一次更新', desc: '工作人员替换了第二页内容，替换动作被侧机位完整拍下。', tone: 'key' },
      { time: 'T-00:00', title: '开场，台下保持统一坐姿', desc: '此时右侧第二排尚无异常动作。', tone: 'normal' },
      { time: 'T+18:40', title: '限定词出现前 0.8 秒的抬头', desc: '视线方向指向右侧第二排，这是全场第一个提前反应信号。', tone: 'key' },
      { time: 'T+18:43', title: '3 秒停顿', desc: '咽回后半句，提示板被放下，两台机位同时调整。', tone: 'key' },
      { time: 'T+72h', title: '两份口径分歧的声明先后出现', desc: '流程派与个人派各自成文，均只覆盖原话的一半。', tone: 'normal' },
    ],
    foreshadows: [
      { title: '会前被替换的第二页', desc: '提示板第二页在开场前 12 分钟被换过，替换后的内容与最终念出的版本仍有差异。' },
      { title: '被切掉的 0.8 秒', desc: '流传最广的版本从这里开始，恰好让「确认反应」变成了「组织语言」。' },
      { title: '放下提示板的动作', desc: '这个动作出现在停顿之后而非之前，说明改口是即时的，不是预设的。' },
    ],
    connections: [
      { title: '同一个侧机位', a: '发布会现场记录', b: '两天后的补充声明', desc: '两份关键素材出自同一台设备，时间戳可交叉验证，排除了拼接可能。' },
      { title: '同一位对表人', a: '开场前的提示板替换', b: '72 小时后的一线回应', desc: '两次出现同一个场内岗位，说明口径调整并没有换人。' },
      { title: '被两边省略的中间段', a: '流程派声明', b: '个人派声明', desc: '两份文本各自省略的部分拼起来，正好是被咽回去的那半句。' },
    ],
    takeaways: [
      '先对齐原始素材版本，再谈解读，否则所有推理都建立在剪辑之上。',
      '把「动作」和「说法」分开记录，动作的可信度通常更高。',
      '被省略的部分比被强调的部分更值得追。',
      '同一套素材交叉验证，能排除绝大多数误判。',
    ],
  },
  {
    id: 'announcement-order',
    title: '官宣顺序里的暗线：为什么先发声的那个，反而不重要',
    dimension: 'connections',
    summary:
      '三条官宣相隔 41 分钟依次发出。顺序不是随机的，它对应着一条被反复使用的责任划分路径。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20investigation%20mood%2C%20abstract%20timeline%20of%20paper%20cards%20connected%20by%20thin%20amber%20thread%20on%20warm%20linen%20surface%2C%20soft%20directional%20light%2C%20muted%20cream%20and%20burnt%20orange%20tones%2C%20minimal%20still%20life%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-02&orientation=landscape',
    author: '拆解组 · 周黍',
    date: '2026-08-25',
    readTime: '8 分钟',
    heat: 8640,
    tags: ['官宣', '顺序', '责任划分'],
    question: '三条声明的时间差 41 分钟，这个数字是巧合还是刻意？',
    sections: [
      {
        heading: '顺序本身就是信息',
        paragraphs: [
          '大多数人只看内容，最容易被忽略的是发布顺序。三条官宣的时间戳分别是 10:06、10:31、10:47，而第二、三条之间的间隔恰好是第一条到第二条的一半。这种比例关系在过往的联合官宣里反复出现。',
          '当间隔呈现稳定比例时，通常意味着存在一份共同的发布节奏表，而不是各自决定。',
        ],
      },
      {
        heading: '先发不等于主动',
        paragraphs: [
          '先发的账号体量最大，但它承担的是「对冲」功能：先把最容易引发追问的部分说完，后面两条才能以补充的姿态出现，降低被逐条追问的概率。',
          '所以在这类事件里，先发者往往是责任最轻的一方，真正的关键信息在第三条。',
        ],
        quote: '顺序靠前的，通常负责铺垫；顺序靠后的，才负责定调。',
      },
      {
        heading: '把六次官宣排在一起看',
        paragraphs: [
          '我们统计了过去两年 6 次类似的多方官宣，5 次呈现「先大后小、间隔递减」的结构。唯一一次例外，恰恰发生在当时已有第三方介入的情况下。',
          '这条规律不保证永远成立，但它提供了判断的起点：看间隔，比看措辞更快。',
        ],
      },
    ],
    timeline: [
      { time: '10:06', title: '第一条官宣（体量最大）', desc: '覆盖最容易被追问的部分，语气克制，未提时间点。', tone: 'normal' },
      { time: '10:31', title: '第二条官宣（间隔 25 分钟）', desc: '以补充口吻出现，首次出现具体时间点。', tone: 'normal' },
      { time: '10:47', title: '第三条官宣（间隔 16 分钟）', desc: '信息密度最高，且首次给出责任归属的措辞。', tone: 'key' },
      { time: '14:10', title: '第一条例外回应', desc: '第三条被追问后，由体量最小的账号出面作了一次刻意模糊的回应。', tone: 'key' },
    ],
    foreshadows: [
      { title: '递减的间隔', desc: '25 分钟与 16 分钟的比例，在过往联合官宣里出现过 5 次。' },
      { title: '只有第三条给时间点', desc: '时间点集中在最后一条，说明定调权在末端而非开头。' },
      { title: '评论区开放状态不同', desc: '只有第三条的评论区在发布后 4 分钟内被限制，说明它才是真正的压力点。' },
    ],
    connections: [
      { title: '同一份节奏表', a: '三条官宣的间隔比例', b: '过往 5 次多方官宣', desc: '间隔结构高度一致，指向共同的发布排期机制。' },
      { title: '同一个措辞模板', a: '第三条的责任措辞', b: '两年前的一次联合说明', desc: '关键短句完全一致，可直接对照责任划分类型。' },
      { title: '被限制的评论区', a: '第三条官宣', b: '下午的补充回应', desc: '两处压力点重合，说明关注焦点从未在第一、二条。' },
    ],
    takeaways: [
      '看时间差不看时间点，间隔比例比内容更早暴露结构。',
      '先发者不一定是主角，先发常常是为了降低追问密度。',
      '把本次官宣和历史同类官宣并列，规律会比单次判断更可靠。',
      '评论区的开放状态，是判断真实压力点的快捷指标。',
    ],
  },
  {
    id: 'deleted-post',
    title: '被删掉的那条动态：48 小时后出现的三个巧合',
    dimension: 'timeline',
    summary:
      '一条存在了 6 小时的动态被删除。删除动作很小，但之后 48 小时里出现的三个巧合，把它的位置补了回来。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20still%20life%20of%20an%20empty%20notice%20board%20with%20a%20single%20removed%20pin%2C%20warm%20muted%20cream%20and%20deep%20rust%20palette%2C%20soft%20window%20light%2C%20quiet%20minimal%20composition%2C%20subtle%20film%20grain%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-03&orientation=landscape',
    author: '拆解组 · 蒋回',
    date: '2026-08-21',
    readTime: '7 分钟',
    heat: 7930,
    tags: ['删帖', '巧合', '时间线'],
    question: '一条只存在 6 小时的动态，为什么会被后发生的三件事重新拼出来？',
    sections: [
      {
        heading: '先确认它确实存在过',
        paragraphs: [
          '删除的内容无法直接查看，但外部痕迹不会一起消失：转发缓存、截图时间戳、以及同一账号在该时段的其他操作记录，三条线索可以互相锁定它的大致内容范围。',
          '我们没有去复原全文，只确认了两件事：它的发布时间，以及它引用的那个日期。',
        ],
      },
      {
        heading: '三个巧合都指向同一个日期',
        paragraphs: [
          '第一个巧合：删除后 9 小时，另一个账号在无关话题里提到了同一个日期。第二个巧合：删除后 27 小时，一份公开材料里出现了该日期作为分界点。第三个巧合：删除后 48 小时，最初的那个账号重新发声，措辞里保留了那一天的限定语。',
          '单个巧合可以忽略，三个巧合落在同一天，就不再适合用随机解释。',
        ],
        quote: '删除改变的是可见性，不改变时间线。时间线一旦对齐，删除反而成了标记。',
      },
      {
        heading: '为什么这条动态重要',
        paragraphs: [
          '它出现在整个事件最安静的阶段。安静期的动作通常信息量最大，因为此时发言不受热度驱动，更接近真实节奏。',
          '这也是我们建议普通读者养成的习惯：越是没人讨论的那几小时，越值得记一笔。',
        ],
      },
    ],
    timeline: [
      { time: 'D1 09:14', title: '动态发布', desc: '内容简短，引用了后续成为关键分界的那个日期。', tone: 'key' },
      { time: 'D1 15:20', title: '动态被删除', desc: '存在 6 小时 6 分钟，删除时无任何说明。', tone: 'key' },
      { time: 'D2 00:32', title: '巧合一：无关话题里出现同一日期', desc: '来自另一个长期活跃账号，语气随意。', tone: 'normal' },
      { time: 'D2 18:05', title: '巧合二：公开材料使用该日期作分界', desc: '首次以正式文本形式确认这个时间点的意义。', tone: 'key' },
      { time: 'D3 09:00', title: '巧合三：原账号重新发声', desc: '保留限定语，间接承认了此前引用的日期。', tone: 'normal' },
    ],
    foreshadows: [
      { title: '被删除时无人讨论', desc: '删除发生在热度最低的时段，说明发起方更在意长期记录而非当下反应。' },
      { title: '限定语在第三次发声中被保留', desc: '一般道歉或补充会清空旧措辞，这里反而保留，说明它是有意留下的锚点。' },
      { title: '第一个巧合来自长期活跃账号', desc: '该账号此前从不涉足同类话题，突然出现一次更可能是被安排的时间标记。' },
    ],
    connections: [
      { title: '同一个日期', a: '被删动态', b: '正式的公开材料', desc: '两者相隔 27 小时，把该日期从私人语境抬升为正式分界。' },
      { title: '同一段沉默期', a: '删除动作', b: '热度最低的 9 小时', desc: '两者时间重合，说明动作选择在关注度低点执行。' },
      { title: '保留的限定语', a: '原始动态', b: '48 小时后的重新发声', desc: '措辞延续，等于用间接方式确认了最初引用。' },
    ],
    takeaways: [
      '删除不是删掉时间，只是删掉可见性，时间线依然可对齐。',
      '关注安静期的动作，信息密度通常高于高热期。',
      '至少需要三个独立巧合才值得上升为线索。',
      '措辞是否延续，往往能反推出原始信息的真假。',
    ],
  },
  {
    id: 'poster-corner',
    title: '海报角落的第三个人：一张图里藏了三处伏笔',
    dimension: 'foreshadow',
    summary:
      '官方物料看起来只是换了排版，但三个被当作装饰处理的元素，在后来的内容里逐一被验证。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20composition%20of%20stacked%20minimal%20poster%20proof%20sheets%20with%20registration%20marks%20on%20a%20warm%20cream%20desk%2C%20amber%20accent%20lighting%2C%20soft%20shadows%2C%20muted%20rust%20and%20sand%20palette%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-04&orientation=landscape',
    author: '拆解组 · 苏待',
    date: '2026-08-18',
    readTime: '6 分钟',
    heat: 7120,
    tags: ['物料', '排版', '伏笔'],
    question: '当官方物料把某个位置留白或放上无关元素时，到底在传达什么？',
    sections: [
      {
        heading: '留白不是失误',
        paragraphs: [
          '这张海报右侧有一段明显不自然的留白。设计上它破坏了视觉平衡，而官方在 3 小时后发布高清版时，留白依旧存在。刻意保留的不平衡，通常不是失误。',
          '对比同一时期的其他物料，这段留白的宽度与一个人物剪影的肩宽基本吻合。',
        ],
      },
      {
        heading: '三处被当成装饰的处理',
        paragraphs: [
          '第一处是留白。第二处是底部一行字号极小的日期，它并不对应任何已公布安排。第三处是画面角落的一个重复符号，此前只在另一条内容线里出现过一次。',
          '后来三处分别被验证：留白位置出现了新角色，小字日期成了档期节点，重复符号连起了两条原本被认为无关的内容线。',
        ],
        quote: '伏笔的识别标准只有一个：它当时看起来多余。',
      },
      {
        heading: '怎么判断是伏笔而不是巧合',
        paragraphs: [
          '我们的判断标准是三个条件同时满足：多余、可被复现、且后续有对应。这条海报三处全部满足。',
          '如果只满足前两条，更可能是设计习惯；只有第三条也成立，才值得提前记录。',
        ],
      },
    ],
    timeline: [
      { time: '第 1 天', title: '首版海报发布', desc: '右侧留白、底部小字日期、角落重复符号同时出现。', tone: 'key' },
      { time: '第 1 天 +3h', title: '高清版发布，三处处理保持不变', desc: '证明并非导出失误。', tone: 'normal' },
      { time: '第 9 天', title: '新角色官宣', desc: '位置与海报留白完全吻合。', tone: 'key' },
      { time: '第 12 天', title: '档期节点公布', desc: '与海报底部小字日期一致。', tone: 'normal' },
    ],
    foreshadows: [
      { title: '不自然的右侧留白', desc: '破坏视觉平衡却被保留到高清版，宽度与人物肩宽吻合。' },
      { title: '底部无人对应的小字日期', desc: '发布时并无任何安排与该日期相关。' },
      { title: '角落的重复符号', desc: '此前只出现过一次的图形，在这里被再次使用。' },
    ],
    connections: [
      { title: '留白与新角色', a: '首版海报', b: '第 9 天官宣', desc: '空间预留与人物公布形成一一对应。' },
      { title: '小字日期与档期', a: '海报底部', b: '第 12 天节点', desc: '日期完全一致，确认其为预告而非装饰。' },
      { title: '重复符号与另一条内容线', a: '角落图形', b: '另一条内容线的旧物料', desc: '图形复用把两条线隐性绑定。' },
    ],
    takeaways: [
      '多余的视觉元素，往往是唯一值得记下来的元素。',
      '判断伏笔要同时满足多余、可复现、有对应三个条件。',
      '高清版是否修正，是区分失误与有意的最好方式。',
      '符号的复用频率，能反映内容线之间的真实关系。',
    ],
  },
  {
    id: 'funding-window',
    title: '融资公告的前夜：时间线上被压缩的 72 小时',
    dimension: 'timeline',
    summary:
      '公告只写了一行日期，但这行日期之前释放的 72 小时里，有五个动作被压缩在了很短的时间窗内。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20desk%20scene%20with%20a%20simple%20folded%20chart%20and%20a%20brass%20clock%20on%20warm%20sand%20paper%2C%20amber%20directional%20light%2C%20muted%20cream%20and%20deep%20rust%20palette%2C%20calm%20minimal%20still%20life%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-05&orientation=landscape',
    author: '拆解组 · 陆迟',
    date: '2026-08-14',
    readTime: '8 分钟',
    heat: 6580,
    tags: ['时间窗', '公告', '节奏'],
    question: '公告当天看起来什么都没有发生，为什么前 72 小时反而挤满了动作？',
    sections: [
      {
        heading: '公告只是结果，节奏藏在之前',
        paragraphs: [
          '公告本身没有任何异常：格式标准，措辞克制，时间很短。把所有相关动作按小时排开后可以看到，真正的密度集中在前 72 小时。',
          '那 72 小时里有 5 个动作，平均间隔不到 15 小时，远高于该主体过去半年的动作频率。',
        ],
      },
      {
        heading: '被压缩的时间窗意味着什么',
        paragraphs: [
          '频率突然升高，通常说明存在一个外部截止点。动作越密集，说明截止点越近。',
          '值得注意的是，5 个动作中有 4 个发生在工作时间之外。把时间点单独拎出来看，工作时间的缺失本身也是一种信息。',
        ],
        quote: '当一件事只在非工作时间推进，它多半不希望被同时段的其他动作注意到。',
      },
      {
        heading: '如何验证',
        paragraphs: [
          '验证方法很简单：把该主体过去半年的所有公开动作排成频率曲线，与这 72 小时对比。如果对比结果是数倍差距，就值得把它当作一个独立节点来记录。',
          '我们不做动机结论，只做节奏确认。节奏是客观的，动机不是。',
        ],
      },
    ],
    timeline: [
      { time: 'T-72h 22:40', title: '动作一（非工作时间）', desc: '一次极短的更新，未附说明。', tone: 'key' },
      { time: 'T-56h 07:05', title: '动作二', desc: '唯一发生在早间的动作。', tone: 'normal' },
      { time: 'T-30h 23:10', title: '动作三（非工作时间）', desc: '与动作一的表述高度相似。', tone: 'normal' },
      { time: 'T-11h 01:25', title: '动作四（非工作时间）', desc: '间隔最短的一次，仅 11 小时。', tone: 'key' },
      { time: 'T-0h 10:00', title: '公告发布', desc: '内容标准，措辞克制，未解释前置动作。', tone: 'normal' },
    ],
    foreshadows: [
      { title: '被避开的工作时间', desc: '5 个动作里 4 个在非工作时间，说明推进方在控制可见度。' },
      { title: '动作一与动作三表述高度相似', desc: '相隔 42 小时却使用近似措辞，说明经过统一处理。' },
      { title: '公告对前置动作零提及', desc: '顺序上更早的动作反而没被引用，说明它们不打算进入官方叙事。' },
    ],
    connections: [
      { title: '同一个时间窗', a: '4 个非工作时间动作', b: 'T-72h 起算', desc: '全部落在同一窗口内，说明共享同一个截止点。' },
      { title: '相似的措辞', a: '动作一', b: '动作三', desc: '用语接近，指向同一套撰写口径。' },
      { title: '频率对比', a: '这 72 小时', b: '过去半年的动作曲线', desc: '密度差距数倍，确认其为异常节点。' },
    ],
    takeaways: [
      '公告是结果，节奏才是可以被观察的部分。',
      '非工作时间的动作，信息价值通常高于白天的动作。',
      '用频率曲线对比，比逐条解读单次动作更可靠。',
      '确认节奏不等于推断动机，前者可验证，后者不可。',
    ],
  },
  {
    id: 'cross-shareholders',
    title: '联名背后的交叉股东：两条看似无关的官宣指向同一个名字',
    dimension: 'connections',
    summary:
      '两条联名官宣隔了两周，品类不同、受众不同。把它们的合作方名单叠在一起，中间出现了同一个名字。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20network%20of%20paper%20labels%20pinned%20to%20a%20warm%20cream%20board%20with%20amber%20threads%2C%20soft%20shadow%2C%20minimal%20muted%20rust%20and%20sand%20palette%2C%20quiet%20documentary%20still%20life%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-06&orientation=landscape',
    author: '拆解组 · 何拾',
    date: '2026-08-09',
    readTime: '7 分钟',
    heat: 5940,
    tags: ['联名', '交叉点', '图谱'],
    question: '两条毫无交集的联名，为什么名单里会重复出现同一个名字？',
    sections: [
      {
        heading: '先叠名单，再谈关系',
        paragraphs: [
          '两条联名官宣的品类、档期、受众都不同，正常判断是毫无关联。但把两份合作方名单叠在一起，出现了一个此前从未被讨论过的共同名字。',
          '这个名字在两次合作里都不是主合作方，位置靠后，容易被忽略。',
        ],
      },
      {
        heading: '次要位置的名字更重要',
        paragraphs: [
          '在联名结构里，排在后面的合作方往往承担的是资源对接，而不是品牌曝光。这种角色反而更可能连续出现在不同项目中。',
          '连续出现两次不足以定性，但它足以构成一条需要记录的连线。',
        ],
        quote: '图谱的价值不在于结论，而在于把「看起来无关」先标成「可能有关」。',
      },
      {
        heading: '图谱怎么用',
        paragraphs: [
          '我们把两次联名的所有参与方画成两张图，只保留重复节点。第二次重叠时，连线就自然浮现，不需要任何猜测。',
          '这也是我们常说的：关联是被发现的，不是被推断的。',
        ],
      },
    ],
    timeline: [
      { time: '第 1 周', title: '联名 A 官宣', desc: '合作方名单共 7 个，共同名字排在第 5 位。', tone: 'normal' },
      { time: '第 3 周', title: '联名 B 官宣', desc: '合作方名单共 6 个，共同名字排在第 4 位。', tone: 'normal' },
      { time: '第 3 周 +1d', title: '名单叠加', desc: '唯一重复节点浮现，两图连成一线。', tone: 'key' },
      { time: '第 5 周', title: '第二次重叠', desc: '同一名字出现在第三个项目中，连线被加强。', tone: 'key' },
    ],
    foreshadows: [
      { title: '两次都排在名单后段', desc: '位置靠后说明其角色是资源对接而非品牌曝光。' },
      { title: '两次官宣相隔恰好两周', desc: '间隔稳定，提示可能存在统一的合作排期。' },
      { title: '第三次重叠提前出现', desc: '在预期之外的时间点出现，说明连接比表面更紧。' },
    ],
    connections: [
      { title: '唯一重复节点', a: '联名 A 名单', b: '联名 B 名单', desc: '两名单叠加后仅一个名字重复。' },
      { title: '第三次验证', a: '联名 A', b: '第三项目', desc: '同一名字再次出现，把偶发提高为模式。' },
      { title: '稳定的两周间隔', a: '联名 A 官宣', b: '联名 B 官宣', desc: '间隔规律性提示排期存在统筹。' },
    ],
    takeaways: [
      '先做名单叠加，再做关系推断。',
      '排在次要位置的合作方，往往是真正的连接点。',
      '至少两次以上重叠，才值得标为一条连线。',
      '图谱用于提出可能，不用于直接下结论。',
    ],
  },
  {
    id: 'apology-revisions',
    title: '致歉信的第六版：措辞变化里藏着的三次让步',
    dimension: 'details',
    summary:
      '同一封信改了六版。真正发生变化的只有三处措辞，而它们恰好对应三次立场后退。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20stack%20of%20revised%20manuscript%20pages%20with%20pencil%20marks%20on%20a%20warm%20cream%20desk%2C%20amber%20window%20light%2C%20soft%20shadow%2C%20muted%20rust%20and%20ink%20palette%2C%20quiet%20minimal%20still%20life%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-07&orientation=landscape',
    author: '拆解组 · 沈既白',
    date: '2026-08-03',
    readTime: '6 分钟',
    heat: 5210,
    tags: ['措辞', '修订', '让步'],
    question: '六次修订里只动了三处词，为什么这三处就能定义整件事的走向？',
    sections: [
      {
        heading: '修订次数多不等于改动大',
        paragraphs: [
          '六版里，段落结构、事实陈述、结尾致意几乎没有变化，改动集中在三个短句上。这种「高频小改」说明分歧不在事实层面，而在立场表述层面。',
          '我们逐版对齐后，只保留差异部分，改动量不到全文的 4%。',
        ],
      },
      {
        heading: '三次让步的顺序',
        paragraphs: [
          '第一次让步是把「如果」改成「因为」，从假设前提转为承认因果。第二次是把被动表述换成主动，明确承担主体。第三次是删掉了原有的限定条件，让表态不再有前置条件。',
          '三次改动方向一致：每改一次，可用空间就更小一点。',
        ],
        quote: '衡量让步的方式不是看语气有多软，而是看限定条件被删掉多少。',
      },
      {
        heading: '为什么值得看第六版',
        paragraphs: [
          '最终公布的第六版，恰好删掉了最后一个限定条件。也就是说，在对外可见的文本里，退让已经完成。',
          '如果只看最终版，你只能看到结论；把六版并排，你才能看到让步的节奏。这就是「爆料背后细节」的意义。',
        ],
      },
    ],
    timeline: [
      { time: '第 1 版', title: '使用「如果」的假设前提', desc: '保留完整限定条件，可解释空间最大。', tone: 'normal' },
      { time: '第 3 版', title: '「如果」改为「因为」', desc: '第一次让步，从假设转为承认因果。', tone: 'key' },
      { time: '第 4 版', title: '被动表述改为主动', desc: '第二次让步，明确承担主体。', tone: 'key' },
      { time: '第 6 版', title: '删除最后一个限定条件', desc: '第三次让步，表态不再有前置条件。', tone: 'key' },
    ],
    foreshadows: [
      { title: '段落结构始终不变', desc: '六版事实陈述几乎一致，说明分歧集中在表述层面。' },
      { title: '改动量不到 4%', desc: '极小改动却覆盖全部关键立场，说明每一处都经过权衡。' },
      { title: '第一次让步出现在第 3 版', desc: '前两版近乎相同，说明真正讨论从第 3 版才开始。' },
    ],
    connections: [
      { title: '三次同向改动', a: '第 3 版', b: '第 6 版', desc: '方向一致，限定条件被逐步删除。' },
      { title: '不变的事实段', a: '第 1 版', b: '第 6 版', desc: '事实陈述始终一致，可排除事实层面的争议。' },
      { title: '主动语态与承担主体', a: '第 4 版措辞', b: '最终版结构', desc: '语态改动延续到最终版，是让步路径的中段。' },
    ],
    takeaways: [
      '高频小改往往说明分歧在表述，而不在事实。',
      '衡量让步看限定条件，不看语气。',
      '并排所有版本，节奏才会显现。',
      '只读最终版，等于只看结论。',
    ],
  },
  {
    id: 'final-frame',
    title: '大结局最后一分钟：三个伏笔是如何被同时回收的',
    dimension: 'foreshadow',
    summary:
      '所有人都在讨论最后一句话。真正完成闭合的，是镜头里三个只出现了几帧的物件。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20still%20life%20of%20three%20small%20objects%20arranged%20in%20a%20row%20on%20warm%20cream%20cloth%2C%20amber%20rim%20light%2C%20deep%20rust%20and%20sand%20palette%2C%20minimal%20documentary%20composition%2C%20soft%20shadow%2C%20high%20detail&width=1280&height=800&seq=bao-dossier-08&orientation=landscape',
    author: '拆解组 · 郑观',
    date: '2026-07-29',
    readTime: '7 分钟',
    heat: 6220,
    tags: ['镜头', '回收', '物件'],
    question: '三个只出现几帧的物件，为什么能决定整个结局是否成立？',
    sections: [
      {
        heading: '先回到第一个镜头',
        paragraphs: [
          '第 1 集的开场镜头里出现过三个物件，其中两个在后续被视为道具失误而被忽略。把它们按出现时间排开，你会发现第六集、第十一集各出现过一次。',
          '也就是说，它们不是道具失误，而是被系统性复用的元素。',
        ],
      },
      {
        heading: '最后一分钟的三次回收',
        paragraphs: [
          '最后一分钟里，三个物件依次入画，每个只停留几帧，间隔约 7 秒。回收顺序与首次出现顺序完全相反。',
          '这种镜像式回收在叙事结构里非常少见，它同时闭合了三条支线，也解释了为什么结局节奏显得偏快。',
        ],
        quote: '把回收顺序倒过来写，是在暗示这三条线本来属于同一条。',
      },
      {
        heading: '怎么自查',
        paragraphs: [
          '最简单的自查方式：把你想验证的元素名称记下来，再按集数顺序检索它出现的每一帧。如果出现了三次以上并集中在首尾两端，基本可以确认是结构性伏笔。',
          '这也是「被忽略的伏笔」这个维度最实用的方法，不依赖解读，只依赖出现频次与位置。',
        ],
      },
    ],
    timeline: [
      { time: '第 1 集', title: '三个物件同框出现', desc: '其中两个被普遍当作道具失误。', tone: 'key' },
      { time: '第 6 集', title: '物件二复现', desc: '作为背景元素出现，仅两帧。', tone: 'normal' },
      { time: '第 11 集', title: '物件三复现', desc: '位置从画面右侧移到左侧。', tone: 'normal' },
      { time: '最后一分钟', title: '三物件镜像回收', desc: '间隔约 7 秒，顺序与首次出现完全相反。', tone: 'key' },
    ],
    foreshadows: [
      { title: '第一个镜头里的第三个物件', desc: '被当作道具失误，却在第 11 集再次出现。' },
      { title: '物件三的位置变化', desc: '从右到左的位移，暗示其叙事归属发生转移。' },
      { title: '回收顺序完全倒置', desc: '镜像结构说明三条支线原属同源。' },
    ],
    connections: [
      { title: '首尾镜像', a: '第 1 集开场', b: '最后一分钟', desc: '出现顺序与回收顺序完全相反。' },
      { title: '中间两次复现', a: '第 6 集', b: '第 11 集', desc: '把首尾两端连接成完整链条。' },
      { title: '位置位移', a: '物件三的初始位置', b: '第 11 集位置', desc: '位移暗示叙事归属被重新分配。' },
    ],
    takeaways: [
      '把元素按出现集数排开，结构会自己浮现。',
      '出现三次以上且集中在首尾的元素，通常是结构性伏笔。',
      '回收顺序往往比回收内容更重要。',
      '自查方法不依赖解读，只依赖频次与位置。',
    ],
  },
];